// src/components/DiaryProgressDashboard.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Table, Tag, Space, Typography, Button, Row, Col, Progress, Tooltip } from 'antd';
import { CheckCircleOutlined, CalendarOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { diaryAPI } from '../utils/api';
import { SkeletonLoader } from './SkeletonLoader';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useIsMobile } from '../hooks/useIsMobile';

const { Text } = Typography;

interface DiaryProgress {
  date: string;
  diary_types: string[];
}

const diaryTypeLabels: Record<string, string> = {
  glucose: 'Глюкоза',
  meals: 'Питание',
  weight: 'Вес',
  movements: 'Шевеления',
  symptoms: 'Симптомы',
  abdomen: 'Живот',
  visits: 'Визиты',
  'lab-tests': 'Анализы',
};

const diaryTypeColors: Record<string, string> = {
  glucose: '#ff4d4f',
  meals: '#fa8c16',
  weight: '#42A5F5',
  movements: '#7CB342',
  symptoms: '#722ed1',
  abdomen: '#13c2c2',
  visits: '#eb2f96',
  'lab-tests': '#2f54eb',
};

const diaryTypeIcons: Record<string, string> = {
  glucose: '🩸',
  meals: '🍎',
  weight: '⚖️',
  movements: '👶',
  symptoms: '🤒',
  abdomen: '📏',
  visits: '🏥',
  'lab-tests': '🧪',
};

// All diary types in order
const allDiaryTypes = ['glucose', 'meals', 'weight', 'movements', 'symptoms', 'abdomen', 'visits', 'lab-tests'];

export const DiaryProgressDashboard: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<DiaryProgress[]>([]);
  const [loading, setLoading] = useState(false);
  // Show only last 5 days
  const [dateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(4, 'day'), // 5 days total (today + 4 previous days)
    dayjs(),
  ]);

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      const [fromDate, toDate] = dateRange;
      const response = await diaryAPI.getDiaryProgress({
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
      });
      setProgress((response.data || []) as DiaryProgress[]);
    } catch (error: any) {
      // 403 is expected if user is not a patient; timeout is often network-related
      const is403 = error?.response?.status === 403;
      const isTimeout = error?.code === 'ECONNABORTED';
      if (!is403 && !isTimeout) {
        console.error('Error loading diary progress:', error);
      }
      setProgress([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Navigate to diary page
  const navigateToDiary = (diaryType: string) => {
    const diaryRoutes: Record<string, string> = {
      glucose: '/diary/glucose',
      meals: '/diary/nutrition',
      weight: '/diary/weight',
      movements: '/diary/movements',
      symptoms: '/diary/symptoms',
      abdomen: '/diary/abdomen',
      visits: '/diary/visits',
      'lab-tests': '/diary/lab-tests',
    };
    const route = diaryRoutes[diaryType];
    if (route) {
      navigate(route);
    }
  };

  // Calculate statistics
  // Count diary types
  const diaryTypeCounts: Record<string, number> = {};
  progress.forEach((item) => {
    item.diary_types.forEach((type) => {
      diaryTypeCounts[type] = (diaryTypeCounts[type] || 0) + 1;
    });
  });

  // Create a map of date -> diary types for quick lookup
  const progressMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    progress.forEach((item) => {
      map.set(item.date, new Set(item.diary_types));
    });
    return map;
  }, [progress]);

  // Generate all dates in range
  const allDates = useMemo(() => {
    const [fromDate, toDate] = dateRange;
    const start = fromDate.toDate();
    const end = toDate.toDate();
    return eachDayOfInterval({ start, end }).reverse(); // Newest first
  }, [dateRange]);

  // Calculate completion percentage for each date
  const getDateCompletion = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filledTypes = progressMap.get(dateStr) || new Set();
    return {
      filled: filledTypes.size,
      total: allDiaryTypes.length,
      percentage: (filledTypes.size / allDiaryTypes.length) * 100,
      types: filledTypes,
    };
  };

  const columns = [
    {
      title: 'Дата',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => {
        const dateObj = parseISO(date);
        const dayName = format(dateObj, 'EEEE', { locale: ru });
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{format(dateObj, 'dd.MM.yyyy')}</div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'capitalize' }}>
              {dayName}
            </div>
          </div>
        );
      },
      sorter: (a: DiaryProgress, b: DiaryProgress) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Прогресс',
      key: 'progress',
      width: 150,
      render: (_: any, record: DiaryProgress) => {
        const completion = getDateCompletion(parseISO(record.date));
        return (
          <div>
            <Progress
              percent={completion.percentage}
              size="small"
              strokeColor={completion.percentage === 100 ? '#7CB342' : completion.percentage >= 50 ? '#faad14' : '#ff4d4f'}
              format={() => `${completion.filled}/${completion.total}`}
            />
          </div>
        );
      },
    },
    ...allDiaryTypes.map((type) => {
      return {
        title: (
          <Tooltip title={diaryTypeLabels[type]}>
            <div style={{ textAlign: 'center', cursor: 'help' }}>
              <div style={{ fontSize: '18px' }}>{diaryTypeIcons[type]}</div>
              {!isMobile && (
                <div style={{ fontSize: '11px', marginTop: '4px' }}>{diaryTypeLabels[type]}</div>
              )}
            </div>
          </Tooltip>
        ),
        key: type,
        width: isMobile ? 50 : 80,
        align: 'center' as const,
        render: (_: any, record: DiaryProgress) => {
          const dateStr = record.date;
          const filledTypes = progressMap.get(dateStr) || new Set();
          const isFilled = filledTypes.has(type);
          const recordDate = parseISO(record.date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const recordDateOnly = new Date(recordDate);
          recordDateOnly.setHours(0, 0, 0, 0);
          const isToday = recordDateOnly.getTime() === today.getTime();
          const isClickable = isToday;
          
          return (
            <Tooltip title={isToday ? (isFilled ? `${diaryTypeLabels[type]} заполнен. Нажмите для просмотра` : `Нажмите, чтобы заполнить ${diaryTypeLabels[type]}`) : 'Заполнение доступно только за сегодня'}>
              <div
                onClick={isClickable ? () => navigateToDiary(type) : undefined}
                style={{
                  cursor: isClickable ? 'pointer' : 'not-allowed',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  opacity: isClickable ? 1 : 0.5,
                }}
                onMouseEnter={(e) => {
                  if (isClickable) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {isFilled ? (
                  <CheckCircleOutlined style={{ fontSize: '20px', color: diaryTypeColors[type] }} />
                ) : (
                  <CloseCircleOutlined style={{ fontSize: '20px', color: '#d9d9d9' }} />
                )}
              </div>
            </Tooltip>
          );
        },
      };
    }),
    {
      title: 'Заполнено',
      key: 'filled',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: DiaryProgress) => {
        const completion = getDateCompletion(parseISO(record.date));
        return (
          <Tag color={completion.percentage === 100 ? 'success' : completion.percentage >= 50 ? 'warning' : 'error'}>
            {completion.filled} / {completion.total}
          </Tag>
        );
      },
      sorter: (a: DiaryProgress, b: DiaryProgress) => {
        const aCompletion = getDateCompletion(parseISO(a.date));
        const bCompletion = getDateCompletion(parseISO(b.date));
        return aCompletion.filled - bCompletion.filled;
      },
    },
  ];

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          <span>Прогресс заполнения дневников</span>
        </Space>
      }
      style={{ borderRadius: '12px' }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Button to navigate to diary */}
        <div style={{ marginBottom: 16, width: '100%' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => navigate('/diary/glucose')}
            style={{
              backgroundColor: '#7CB342',
              borderColor: '#7CB342',
              borderRadius: '8px',
              height: '40px',
              width: '100%',
            }}
            block
          >
            Перейти к заполнению дневника
          </Button>
        </div>

        {/* Progress Table - Show all dates in range */}
        <div>
          {loading ? (
            <SkeletonLoader type="table" rows={5} />
          ) : (
            <>
          <style>{`
            .diary-progress-table .ant-table-tbody > tr.row-complete {
              background-color: #f6ffed !important;
            }
            .diary-progress-table .ant-table-tbody > tr.row-partial {
              background-color: #fffbe6 !important;
            }
            .diary-progress-table .ant-table-tbody > tr.row-started {
              background-color: #fff2e8 !important;
            }
            .diary-progress-table .ant-table-tbody > tr.row-empty {
              background-color: #fafafa !important;
            }
            .diary-progress-table .ant-table-tbody > tr.row-today {
              border-left: 4px solid #7CB342 !important;
              background-color: #fff9f5 !important;
            }
            .diary-progress-table .ant-table-tbody > tr:hover {
              opacity: 0.9;
            }
          `}</style>
          <Table
            className="diary-progress-table"
            dataSource={allDates.map((date) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const filledTypes = progressMap.get(dateStr) || new Set();
              return {
                date: dateStr,
                diary_types: Array.from(filledTypes),
              };
            })}
            columns={columns}
            rowKey="date"
            loading={loading}
            pagination={false}
            locale={{
              emptyText: 'Нет записей за выбранный период',
            }}
            scroll={isMobile ? { x: 'max-content' } : undefined}
            size={isMobile ? 'small' : 'middle'}
            rowClassName={(record) => {
              const completion = getDateCompletion(parseISO(record.date));
              const recordDate = parseISO(record.date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const recordDateOnly = new Date(recordDate);
              recordDateOnly.setHours(0, 0, 0, 0);
              const isToday = recordDateOnly.getTime() === today.getTime();
              
              let className = '';
              if (completion.percentage === 100) className = 'row-complete';
              else if (completion.percentage >= 50) className = 'row-partial';
              else if (completion.percentage > 0) className = 'row-started';
              else className = 'row-empty';
              
              if (isToday) className += ' row-today';
              return className;
            }}
          />
            </>
          )}
        </div>
        
        {/* Legend */}
        <Card size="small" style={{ marginTop: 16 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>Легенда:</Text>
            <Row gutter={[16, 8]}>
              {allDiaryTypes.map((type) => (
                <Col key={type} xs={12} sm={8} md={6}>
                  <Space>
                    <span style={{ fontSize: '16px' }}>{diaryTypeIcons[type]}</span>
                    <Text style={{ fontSize: '12px' }}>{diaryTypeLabels[type]}</Text>
                    <CheckCircleOutlined style={{ fontSize: '14px', color: diaryTypeColors[type] }} />
                  </Space>
                </Col>
              ))}
            </Row>
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
              <Space>
                <Text style={{ fontSize: '12px' }}>Цвет строки:</Text>
                <Tag color="success">Все заполнено</Tag>
                <Tag color="warning">Частично заполнено (≥50%)</Tag>
                <Tag color="error">Начато (&lt;50%)</Tag>
                <Tag>Не заполнено</Tag>
              </Space>
            </div>
          </Space>
        </Card>
      </Space>
    </Card>
  );
};
